import React, { useState, useEffect } from 'react';
import '../styles/AdminAccounts.css';

function AdminAccounts() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        is_superadmin: false,
        can_manage_partners: false,
        can_manage_members: false,
        can_manage_events: false,
        can_manage_products: false,
        can_manage_projects: false,
        can_manage_reservations: false,
    });
    const [wasValidated, setWasValidated] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [modal, setModal] = useState({
        show: false, title: '', message: '', type: 'info', onConfirm: null,
    });

    const isUserSuperAdmin = localStorage.getItem('is_superadmin') === 'true';

    useEffect(() => {
        if (isUserSuperAdmin) {
            fetchAdmins();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAdmins = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/admins/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('access_token');
                window.location.href = '/admin/login';
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setAdmins(data);
            } else {
                notify('error', 'Erreur', `Impossible de charger les administrateurs (${response.status}).`);
            }
        } catch (err) {
            notify('error', 'Erreur de connexion', "Impossible de joindre le serveur.");
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
    const notify = (type, title, message) => setModal({ show: true, type, title, message, onConfirm: null });
    const confirm = (title, message, onConfirm) =>
        setModal({ show: true, type: 'warning', title, message, onConfirm });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setWasValidated(true);

        if (!formData.username || (!isEditing && !formData.password)) return;

        try {
            const token = localStorage.getItem('access_token');
            const url = isEditing 
                ? `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/admins/${editingId}/`
                : `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/admins/`;
            
            const method = isEditing ? 'PATCH' : 'POST';
            
            // Si on modifie et que le mot de passe est vide, on ne l'envoie pas
            const payload = { ...formData };
            if (isEditing && !payload.password) {
                delete payload.password;
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('access_token');
                window.location.href = '/admin/login';
                return;
            }

            if (response.ok) {
                notify('success', 'Succès', isEditing ? "Administrateur mis à jour !" : "Administrateur créé avec succès !");
                resetForm();
                fetchAdmins();
            } else {
                const msg = data.password ? data.password[0] : (data.username ? data.username[0] : `Erreur ${response.status}`);
                notify('error', 'Erreur', msg);
            }
        } catch (err) {
            notify('error', 'Erreur', "Une erreur est survenue.");
        }
    };

    const handleEdit = (admin) => {
        setFormData({
            username: admin.username,
            password: '',
            is_superadmin: admin.is_superadmin,
            can_manage_partners: admin.can_manage_partners,
            can_manage_members: admin.can_manage_members,
            can_manage_events: admin.can_manage_events,
            can_manage_products: admin.can_manage_products,
            can_manage_projects: admin.can_manage_projects,
            can_manage_reservations: admin.can_manage_reservations,
        });
        setEditingId(admin.id);
        setIsEditing(true);
        setShowForm(true);
        setWasValidated(false);
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetForm = () => {
        setFormData({
            username: '',
            password: '',
            is_superadmin: false,
            can_manage_partners: false,
            can_manage_members: false,
            can_manage_events: false,
            can_manage_products: false,
            can_manage_projects: false,
            can_manage_reservations: false,
        });
        setIsEditing(false);
        setEditingId(null);
        setWasValidated(false);
        setShowForm(false);
    };

    const handleDelete = (id) => {
        confirm('Supprimer l\'administrateur', 'Voulez-vous vraiment supprimer ce compte ?', async () => {
            try {
                const token = localStorage.getItem('access_token');
                const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/admins/${id}/`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    setAdmins(admins.filter(a => a.id !== id));
                    notify('success', 'Supprimé', "Le compte a été supprimé.");
                } else {
                    notify('error', 'Erreur', "Impossible de supprimer ce compte.");
                }
            } catch (err) {
                notify('error', 'Erreur', "Erreur lors de la suppression.");
            }
        });
    };

    const currentUsername = localStorage.getItem('username') || '';

    const handleDeleteSelf = (id) => {
        confirm(
            'Supprimer mon compte',
            'Vous allez supprimer votre propre compte. Vous serez déconnecté immédiatement. Confirmer ?',
            async () => {
                try {
                    const token = localStorage.getItem('access_token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/admins/${id}/`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        localStorage.clear();
                        window.location.href = '/admin/login';
                    } else {
                        notify('error', 'Erreur', "Impossible de supprimer votre compte.");
                    }
                } catch {
                    notify('error', 'Erreur', "Erreur lors de la suppression.");
                }
            }
        );
    };

    if (!isUserSuperAdmin) {
        return (
            <div className="admin-accounts-container access-denied">
                <div className="denied-card">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><line x1="8" y1="11" x2="16" y2="11" /></svg>
                    <h3>Accès Refusé</h3>
                    <p>Cette page est réservée aux Super-Administrateurs.</p>
                </div>
            </div>
        );
    }

    const IconTrash = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
    );

    const IconEdit = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    );

    const IconEye = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
        </svg>
    );
    const IconEyeOff = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    );

    return (
        <div className="admin-accounts-container">
            {modal.show && (
                <div className="tiwizi-modal-overlay" onClick={closeModal}>
                    <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
                        <div className={`tiwizi-modal-icon ${modal.type === 'success' ? 'success' : modal.type === 'warning' ? 'warning' : 'error'}`}>
                            {modal.type === 'success'
                                ? <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                : <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                            }
                        </div>
                        <h3>{modal.title}</h3>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{modal.message}</p>
                        <div className="tiwizi-modal-actions">
                            {modal.onConfirm ? (
                                <>
                                    <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
                                    <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
                                </>
                            ) : (
                                <button className="tiwizi-btn-cancel" onClick={closeModal}>Fermer</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <section className="data-section">
                <div className="section-title-group">
                    <h3>Gestion des Administrateurs <span className="count-badge">{admins.length}</span></h3>
                    <div className="section-title-actions">
                        {admins.find(a => a.username === currentUsername) && (
                            <button
                                className="btn-delete-self-bottom"
                                onClick={() => handleDeleteSelf(admins.find(a => a.username === currentUsername).id)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                                Supprimer mon compte
                            </button>
                        )}
                        <button className="btn-add" onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}>
                            {showForm ? 'Annuler' : '+ Nouvel Admin'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="admin-form-card">
                        <h4>{isEditing ? 'Modifier l\'administrateur' : 'Ajouter un nouvel administrateur'}</h4>
                        <form onSubmit={handleSubmit} className={wasValidated ? 'was-validated' : ''} noValidate autoComplete="off">
                            <div className="admin-form-grid">
                                <div className="form-column">
                                    <div className="form-group">
                                        <label>Nom d'utilisateur <span style={{ color: '#e74c3c' }}>*</span></label>
                                        <input
                                            type="text"
                                            value={formData.username}
                                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            placeholder="ex: Ahmed_Admin"
                                            autoComplete="off"
                                            required
                                        />
                                    </div>
                                    <div className="form-group checkbox-group">
                                        <label className="checkbox-item">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_superadmin}
                                                onChange={(e) => setFormData({ ...formData, is_superadmin: e.target.checked })}
                                            />
                                            <strong>Est Superadmin</strong>
                                        </label>
                                    </div>
                                </div>
                                <div className="form-column">
                                    <div className="form-group">
                                        <div className="label-with-info">
                                            <label>Mot de passe <span style={{ color: '#e74c3c' }}>*</span></label>
                                            <div className="info-icon-wrapper">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f5c50a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="info-svg">
                                                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                                                </svg>
                                                <div className="criteria-tooltip">
                                                    <strong>Critères requis :</strong>
                                                    <ul>
                                                        <li>Minimum 8 caractères</li>
                                                        <li>Au moins un chiffre ou symbole</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                placeholder={isEditing ? "Laissez vide pour inchangé" : "Entrez un mot de passe"}
                                                autoComplete="new-password"
                                                required={!isEditing}
                                            />
                                            <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <IconEyeOff /> : <IconEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!formData.is_superadmin && (
                                <div className="permissions-box-container">
                                    <div className="permissions-box">
                                        <p className="permissions-title">Droits d'accès :</p>
                                        <div className="permissions-grid">
                                            <div className={`permission-item ${formData.can_manage_partners ? 'active' : ''}`} onClick={() => setFormData({ ...formData, can_manage_partners: !formData.can_manage_partners })}>
                                                <div className="permission-info">
                                                    <span className="permission-label">Partenaires</span>
                                                    <span className="permission-desc">Gérer les demandes et infos</span>
                                                </div>
                                                <div className="permission-toggle-wrapper">
                                                    <input type="checkbox" checked={formData.can_manage_partners} readOnly />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </div>

                                            <div className={`permission-item ${formData.can_manage_members ? 'active' : ''}`} onClick={() => setFormData({ ...formData, can_manage_members: !formData.can_manage_members })}>
                                                <div className="permission-info">
                                                    <span className="permission-label">Membres</span>
                                                    <span className="permission-desc">Gérer les adhérents et profils</span>
                                                </div>
                                                <div className="permission-toggle-wrapper">
                                                    <input type="checkbox" checked={formData.can_manage_members} readOnly />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </div>

                                            <div className={`permission-item ${formData.can_manage_events ? 'active' : ''}`} onClick={() => setFormData({ ...formData, can_manage_events: !formData.can_manage_events })}>
                                                <div className="permission-info">
                                                    <span className="permission-label">Événements</span>
                                                    <span className="permission-desc">Planifier et modifier les events</span>
                                                </div>
                                                <div className="permission-toggle-wrapper">
                                                    <input type="checkbox" checked={formData.can_manage_events} readOnly />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </div>

                                            <div className={`permission-item ${formData.can_manage_products ? 'active' : ''}`} onClick={() => setFormData({ ...formData, can_manage_products: !formData.can_manage_products })}>
                                                <div className="permission-info">
                                                    <span className="permission-label">Produits</span>
                                                    <span className="permission-desc">Ajouter et modifier les produits</span>
                                                </div>
                                                <div className="permission-toggle-wrapper">
                                                    <input type="checkbox" checked={formData.can_manage_products} readOnly />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </div>

                                            <div className={`permission-item ${formData.can_manage_projects ? 'active' : ''}`} onClick={() => setFormData({ ...formData, can_manage_projects: !formData.can_manage_projects })}>
                                                <div className="permission-info">
                                                    <span className="permission-label">Projets</span>
                                                    <span className="permission-desc">Créer et gérer les projets</span>
                                                </div>
                                                <div className="permission-toggle-wrapper">
                                                    <input type="checkbox" checked={formData.can_manage_projects} readOnly />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </div>

                                            <div className={`permission-item ${formData.can_manage_reservations ? 'active' : ''}`} onClick={() => setFormData({ ...formData, can_manage_reservations: !formData.can_manage_reservations })}>
                                                <div className="permission-info">
                                                    <span className="permission-label">Réservations</span>
                                                    <span className="permission-desc">Consulter et gérer les réservations</span>
                                                </div>
                                                <div className="permission-toggle-wrapper">
                                                    <input type="checkbox" checked={formData.can_manage_reservations} readOnly />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-actions">
                                {isEditing && (
                                    <button type="button" className="btn-cancel-edit" onClick={resetForm}>Annuler</button>
                                )}
                                <button type="submit" className="btn-submit">
                                    {isEditing ? 'Enregistrer les modifications' : 'Créer le compte'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <p className="loading-text">Chargement...</p>
                ) : (
                    <div className="admin-cards-grid">
                        {admins.map((admin) => (
                            <div className={`admin-card smaller${admin.username === currentUsername ? ' is-self' : ''}`} key={admin.id}>
                                <div className="card-avatar smaller">
                                    {admin.username.charAt(0).toUpperCase()}
                                </div>
                                <div className="card-content smaller">
                                    <h4>
                                        {admin.username}
                                        {admin.username === currentUsername && (
                                            <span className="badge-you">Vous</span>
                                        )}
                                    </h4>
                                    <span className={`role-badge ${admin.is_superadmin ? 'super' : 'standard'}`}>
                                        {admin.is_superadmin ? 'Superadmin' : 'Admin'}
                                    </span>
                                </div>
                                <div className="card-actions smaller">
                                    {!admin.is_superadmin ? (
                                        <>
                                            <button className="btn-edit" onClick={() => handleEdit(admin)} title="Modifier">
                                                <IconEdit />
                                            </button>
                                            <button className="btn-delete" onClick={() => handleDelete(admin.id)} title="Supprimer">
                                                <IconTrash />
                                            </button>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

        </div>
    );
}

export default AdminAccounts;
